import React from 'react';
import { Link } from 'react-router-dom';
import { PRODUCT_LINK_PATTERNS } from '@/lib/blogLinkPatterns';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface RichArticleTextProps {
  text: string;
}

const HANDLE_NAMES: Record<string, string> = {
  'pink-glow-serum-with-pdrn-peptides': 'Pink Glow Serum',
  'pdrn-pink-sheet-masks-10-pack':      'PDRN Sheet Masks',
  'smart-capsule-cream-with-collagen':  'Smart Capsule Cream',
  'complete-pdrn-trio-bundle':          'Complete Trio Bundle',
};

/**
 * Renders a paragraph with contextual product links injected at render-time.
 * Every occurrence of every product keyword in all 11 languages becomes a link.
 */
export function RichArticleText({ text }: RichArticleTextProps) {
  if (PRODUCT_LINK_PATTERNS.length === 0) {
    return <>{text}</>;
  }

  const nodes: React.ReactNode[] = [];
  let remaining = text;
  let nodeKey = 0;

  while (remaining.length > 0) {
    let earliestIndex = -1;
    let earliestLength = 0;
    let earliestHandle = '';
    let earliestMatch = '';

    for (const { pattern, handle } of PRODUCT_LINK_PATTERNS) {
      pattern.lastIndex = 0;
      const match = pattern.exec(remaining);
      if (match === null) continue;

      if (earliestIndex === -1 || match.index < earliestIndex) {
        earliestIndex = match.index;
        earliestLength = match[0].length;
        earliestHandle = handle;
        earliestMatch = match[0];
      }
    }

    if (earliestIndex === -1) {
      nodes.push(remaining);
      break;
    }

    if (earliestIndex > 0) {
      nodes.push(remaining.slice(0, earliestIndex));
    }

    const productName = HANDLE_NAMES[earliestHandle] ?? earliestHandle;

    nodes.push(
      <TooltipProvider key={`tip-${nodeKey}`} delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Link
              to={`/product/${earliestHandle}`}
              className="text-primary font-semibold underline decoration-primary/50 underline-offset-2 bg-primary/10 hover:bg-primary/20 rounded-sm px-0.5 transition-all duration-150 hover:decoration-primary"
            >
              {earliestMatch}
            </Link>
          </TooltipTrigger>
          <TooltipContent side="top" className="text-xs">
            🛍️ {productName}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );

    nodeKey++;
    remaining = remaining.slice(earliestIndex + earliestLength);
  }

  return <>{nodes}</>;
}
